import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Redirect } from 'react-router-dom';

import {
  DocumentUploadAssistantStepEnum,
  useWizardVerificationQuery,
} from '../../../generated/graphql';
import { RouteReferrerState, routes } from '../../../routes/routesConfig';
import Logger from '../../../utils/Logger';
import LoadingPage from '../../LoadingPage/LoadingPage';
import { WizardParentModalLayout } from '../components/WizardParentModalLayout/WizardParentModalLayout';
import { WizardVerificationBackImage } from './step/BackImage/WizardVerificationBackImage';
import { WizardVerificationFinished } from './step/Finished/WizardVerificationFinished';
import { WizardVerificationFrontImage } from './step/FrontImage/WizardVerificationFrontImage';
import { WizardVerificationSelfie } from './step/Selfie/WizardVerificationSelfie';

// Only for testing
export type WizardVerificationStepName =
  | 'backImage'
  | 'frontImage'
  | 'selfie'
  | 'finished';

export type WizardVerificationBasicProps = {
  /** Callback to go to the next step */
  goToNextStep: () => void;
  /** Whether to show the page-specific loader */
  showLoader: boolean;
  /** URL to upload the image to */
  uploadUrl: string;
  /** Closes the wizard and navigates back to the home */
  closeWizard?: () => void;

  nextStepLoading?: boolean;
};

export const WizardVerification: React.FC = () => {
  const { t } = useTranslation('wizardVerification');
  const history = useHistory<RouteReferrerState>();
  //todo: why hard coded?
  const [totalSteps, setTotalSteps] = React.useState(4);

  const { data, loading, error, refetch } = useWizardVerificationQuery({
    onCompleted: (newData) => {
      // Add 1 for the finish step
      const newTotalSteps = newData.documents.tour.totalSteps + 1;
      if (newTotalSteps && newTotalSteps !== totalSteps) {
        setTotalSteps(newTotalSteps);
      }
    },
    notifyOnNetworkStatusChange: true,
  });

  const { state } = history.location;

  function closeWizardHandler() {
    history.push(state?.referrer ?? routes.home.path);
  }

  function urlHasChanged(newUrl: string) {
    return history.location.pathname !== newUrl;
  }

  if (loading && !data) {
    return <LoadingPage />;
  }

  // An error occurred or we have no data
  if (error || !data) {
    Logger.log('An error occurred or we have no data');
    return <Redirect to={{ pathname: routes.error.path, state }} push={true} />;
  }

  let pageContent: JSX.Element | null;
  switch (data.documents.tour.currentStep?.name ?? null) {
    case DocumentUploadAssistantStepEnum.IdentityProofFrontside: {
      const url = routes.wizardVerificationFrontImage.path;
      pageContent = (
        <>
          {urlHasChanged(url) && (
            <Redirect to={{ pathname: url, state }} push={false} />
          )}
          <WizardVerificationFrontImage
            goToNextStep={refetch}
            uploadUrl={data.documents.tour.currentStep!.uploadUrl}
            showLoader={loading}
          />
        </>
      );
      break;
    }
    case DocumentUploadAssistantStepEnum.IdentityProofBackside: {
      const url = routes.wizardVerificationBackImage.path;
      pageContent = (
        <>
          {urlHasChanged(url) && (
            <Redirect to={{ pathname: url, state }} push={false} />
          )}
          <WizardVerificationBackImage
            goToNextStep={refetch}
            uploadUrl={data.documents.tour.currentStep!.uploadUrl}
            showLoader={loading}
          />
        </>
      );
      break;
    }
    case DocumentUploadAssistantStepEnum.IdShot: {
      const url = routes.wizardVerificationSelfie.path;
      pageContent = (
        <>
          {urlHasChanged(url) && (
            <Redirect to={{ pathname: url, state }} push={false} />
          )}
          <WizardVerificationSelfie
            goToNextStep={refetch}
            uploadUrl={data.documents.tour.currentStep!.uploadUrl}
            showLoader={loading}
          />
        </>
      );
      break;
    }
    case null: {
      const url = routes.wizardVerificationFinished.path;
      pageContent = (
        <>
          {urlHasChanged(url) && (
            <Redirect to={{ pathname: url, state }} push={false} />
          )}
          <WizardVerificationFinished closeWizard={closeWizardHandler} />
        </>
      );
      break;
    }
    default:
      pageContent = (
        <Redirect to={state?.referrer ?? routes.home.path} push={true} />
      );
  }

  return (
    <WizardParentModalLayout
      headerText={t('headerText')}
      totalSteps={totalSteps}
      currentStep={data?.documents.tour.currentStep?.position ?? totalSteps}
      onCloseClick={closeWizardHandler}
    >
      {pageContent}
    </WizardParentModalLayout>
  );
};
